import React, { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuList, NavigationMenuItem, NavigationMenuLink } from "@/components/ui/navigation-menu";
import { cn } from "@/lib/utils";
function HeaderContent() {
  const {
    menuItems,
    loading
  } = useMenu();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest('a[href]') as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute('href');
        if (!href) return;
        let hash = '';
        if (href.startsWith('#')) {
          hash = href;
        } else if (href.startsWith('/') && href.includes('#')) {
          const [path, hashPart] = href.split('#');
          if (path === '/' || path === window.location.pathname) {
            hash = '#' + hashPart;
          }
        }
        if (hash && hash !== '#') {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener('click', handleAnchorClick);
    return () => document.removeEventListener('click', handleAnchorClick);
  }, []);
  return <header data-section-id="32202" className={cn("sticky top-0 z-50 w-full transition-all duration-300 border-b", isScrolled ? "bg-background/80 backdrop-blur-md border-transparent shadow-md" : "bg-background border-accent")}>
      <div className="container mx-auto px-4 h-[100px] flex items-center justify-between">
        {}
        <div className="flex-1 flex justify-start">
          <Link to="/" className="block">
            <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1584719/img/image_gk4eoisz.png" alt="Invest KC Property Management" className="h-[60px] w-auto object-contain" />
          </Link>
        </div>

        {}
        <div className="hidden md:flex flex-1 justify-center">
          {loading ? <div className="flex gap-6">
              {[1, 2, 3, 4].map(i => <div key={i} className="h-5 w-20 bg-muted/50 animate-pulse rounded-sm" data-index={i} />)}
            </div> : <NavigationMenu>
              <NavigationMenuList className="gap-8">
                {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
                    <NavigationMenuLink asChild>
                      <Link to={item.href || "#"} className="group relative text-sm font-medium text-foreground transition-colors hover:text-primary py-2 block tracking-tight">
                        {item.label}
                        <span className="absolute bottom-0 left-0 h-[2px] w-full origin-bottom-right scale-x-0 bg-primary transition-transform duration-300 group-hover:origin-bottom-left group-hover:scale-x-100" />
                      </Link>
                    </NavigationMenuLink>
                  </NavigationMenuItem>)}
              </NavigationMenuList>
            </NavigationMenu>}
        </div>

        {}
        <div className="hidden md:flex items-center gap-4 md:mr-8">
          <Button asChild size="lg" className="bg-primary hover:bg-primary/90 text-primary-foreground font-medium shadow-sm rounded-sm transition-all duration-300 hover:scale-[1.02]">
            <Link to="https://renters4u.net/rental-application/">Rental Application</Link>
          </Button>
          <Button asChild size="lg" className="bg-primary hover:bg-primary/90 text-primary-foreground font-medium shadow-sm rounded-sm transition-all duration-300 hover:scale-[1.02]">
            <Link to="https://app.tenantcloud.com/login">Porthole Login</Link>
          </Button>
        </div>

        {}
        <div className="md:hidden">
          <Button variant="ghost" size="icon" className="text-foreground hover:text-primary hover:bg-transparent" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} aria-label="Toggle menu">
            {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </Button>
        </div>
      </div>

      {}
      {isMobileMenuOpen && <div className="md:hidden absolute top-full left-0 w-full bg-background border-b border-border shadow-xl animate-in slide-in-from-top-5 z-40">
          <div className="container mx-auto px-4 py-6 flex flex-col gap-4">
            <nav className="flex flex-col space-y-2">
              {loading ? <div className="space-y-3">
                  {[1, 2, 3, 4].map(i => <div key={i} className="h-8 w-full bg-muted/50 animate-pulse rounded-sm" data-index={i} />)}
                </div> : menuItems.map((item, i) => <div key={item.id} data-index={i}>
                    <Link to={item.href || "#"} className="block text-base font-medium text-foreground hover:text-primary py-3 border-b border-border/40 last:border-0 transition-colors tracking-tight" onClick={() => setIsMobileMenuOpen(false)}>
                      {item.label}
                    </Link>
                  </div>)}
            </nav>
            <div className="pt-2 flex flex-col gap-3">
              <Button asChild className="w-full bg-primary text-primary-foreground h-12 text-base rounded-sm">
                <Link to="https://renters4u.net/rental-application/" onClick={() => setIsMobileMenuOpen(false)}>
                  Rental Application
                </Link>
              </Button>
              <Button asChild className="w-full bg-primary text-primary-foreground h-12 text-base rounded-sm">
                <Link to="https://app.tenantcloud.com/login" onClick={() => setIsMobileMenuOpen(false)}>
                  Porthole Login
                </Link>
              </Button>
            </div>
          </div>
        </div>}
    </header>;
}
export default function Header() {
  return <section id="invest-kc-header" className="bg-background text-foreground relative">
      <MenuProvider menu_id="32">
        <HeaderContent />
      </MenuProvider>
    </section>;
}